/**
 * AppShellV2 — sidebar + top strip matching the "Home" / "AI Assistant" mockups.
 * Scoped to the new Home / AI Assistant / Schedule pages only — the rest of the
 * app keeps using the existing Layout.tsx shell.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronLeft, User, Crown, LogOut, Bell, PanelLeft, Search, ExternalLink, X as XIcon } from 'lucide-react';
import Avatar from '../Avatar.tsx';
import CostBudgetPopover from '../AIConfig/CostBudgetPopover.jsx';
import useCostSummary, { clearCostSummary } from '../../hooks/useCostSummary.ts';
import UpgradeModal from '../modals/UpgradeModal.tsx';
import { getCurrentUserProfile, formatUserProfile } from '../../utils/auth.ts';
import { remindersAPI } from '../../services/api.js';
import PrimarySidebarNav from './PrimarySidebarNav.tsx';

export const T2 = {
  page: 'var(--lyra-color-surface)',
  surface: 'var(--lyra-color-surface)',
  border: 'var(--lyra-color-border)',
  ink: 'var(--lyra-color-text)',
  ink2: 'var(--app-text-2)',
  ink3: 'var(--lyra-color-text-muted)',
  accent: 'var(--lyra-color-primary)',
  accentSoft: 'var(--app-primary-soft)',
  accentText: 'var(--app-primary-deep)',
};

const MOBILE_BREAKPOINT = 768;

export default function AppShellV2({ children, pageTitle, backTo, onLogout }: { children: React.ReactNode; pageTitle?: string; backTo?: string; onLogout?: () => void }) {
  const navigate = useNavigate();
  const userProfile = getCurrentUserProfile();
  const formattedProfile = userProfile ? formatUserProfile(userProfile) : null;

  // Sidebar + top strip collapse into a fixed header + slide-in drawer below
  // this width — same breakpoint and drawer pattern as Layout.tsx, so the
  // mobile nav behaves identically across the whole app.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setIsMobileMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Desktop sidebar collapse — same localStorage key as Layout.tsx's own
  // toggle, so the collapsed/expanded state is shared and consistent
  // whether you're on Home/Schedule or any other page in the app.
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('menuCollapsed');
      return saved ? JSON.parse(saved) : false;
    } catch { return false; }
  });
  const toggleMenuCollapsed = () => {
    setIsMenuCollapsed((prev: boolean) => {
      const next = !prev;
      try { localStorage.setItem('menuCollapsed', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Dark mode for the sidebar + top strip — same 'darkMode' localStorage key
  // + <html class="dark"> toggle Layout.tsx uses, so both stay in sync with
  // the same global toggle everywhere in the app. The T2-themed page content
  // underneath (Home/Schedule's own cards etc.) intentionally stays light —
  // only the chrome (sidebar + top bar) switches, matching what was asked.
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('darkMode');
      return saved ? JSON.parse(saved) : false;
    } catch { return false; }
  });
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);
  const toggleDarkMode = () => {
    setIsDarkMode((prev: boolean) => {
      const next = !prev;
      try { localStorage.setItem('darkMode', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const sidebarDark = {
    bg: T2.surface,
    border: T2.border,
    title: T2.ink,
    hoverBg: T2.page,
    icon: T2.ink2,
  };

  // Same token shape as sidebarDark, for the top strip (header bar + its
  // dropdowns) — kept separate since the two chrome pieces can theoretically
  // diverge, but currently mirror TopBar.tsx's dark palette 1:1.
  const barDark = {
    bg: T2.surface,
    border: T2.border,
    surface: 'var(--lyra-color-surface)',
    ink: T2.ink,
    ink2: T2.ink2,
    ink3: T2.ink3,
    hoverBg: T2.page,
  };

  // Search — same self-contained menu-item search TopBar.tsx has (reads the
  // same config/menuConfig.json), so it's not missing on Home/Schedule.
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ label: string; path: string; category: string }[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);

  const searchMenuItems = (query: string) => {
    if (!query.trim()) return [];
    const results: { label: string; path: string; category: string }[] = [];
    let menuConfig: any = {};
    try { menuConfig = require('../../config/menuConfig.json').menuConfig || {}; } catch { menuConfig = {}; }
    const queryLower = query.toLowerCase();
    const searchInLevel = (items: any, category = '') => {
      Object.entries(items || {}).forEach(([, item]: [string, any]) => {
        if (item.children) {
          searchInLevel(item.children, item.label || category);
        } else if (item.visible && item.path && item.label) {
          const labelWords = item.label.toLowerCase().split(' ');
          const queryWords = queryLower.split(' ');
          const hasMatch = queryWords.some((qw: string) => labelWords.some((lw: string) => lw.includes(qw) || qw.includes(lw)));
          if (hasMatch) results.push({ label: item.label, path: item.path, category });
        }
      });
    };
    searchInLevel(menuConfig);
    return results;
  };

  const handleSearchToggle = () => {
    setIsSearchExpanded((v) => {
      const next = !v;
      if (next) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      } else {
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchResults(false);
      }
      return next;
    });
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    const results = searchMenuItems(query);
    setSearchResults(results);
    setShowSearchResults(query.length > 0 && results.length > 0);
  };
  const handleSearchClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    searchInputRef.current?.focus();
  };
  const handleSearchResultClick = (result: { path: string }) => {
    navigate(result.path);
    setIsSearchExpanded(false);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };
  useEffect(() => {
    const handleClickOutsideSearch = (event: MouseEvent) => {
      if (isSearchExpanded && searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearchExpanded(false);
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideSearch);
    return () => document.removeEventListener('mousedown', handleClickOutsideSearch);
  }, [isSearchExpanded]);

  const [showUsage, setShowUsage] = useState(false);
  const { data: costData, loading: costLoading, hasData: costHasData, refresh: fetchCostSummary } = useCostSummary();
  // Shared with TopBar.tsx's cost pill (same localStorage key) so switching
  // currency in one shell is reflected in the other.
  const [currency, setCurrency] = useState<'USD' | 'INR'>((localStorage.getItem('ai_currency') as 'USD' | 'INR') || 'USD');
  const usageBtnRef = useRef<HTMLButtonElement | null>(null);

  // Profile dropdown — same shape as TopBar.tsx's (View Profile / Upgrade to
  // Pro / Logout) so the account menu behaves identically everywhere.
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);

  // Notification bell — same reminders feed/logic as TopBar.tsx, so it's not
  // missing on Home/AI Assistant/Schedule (the three AppShellV2 pages).
  const [reminders, setReminders] = useState<any[]>([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [showRemindersMenu, setShowRemindersMenu] = useState(false);
  const [seenReminderKeys, setSeenReminderKeys] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('lyra_seen_reminders');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const remindersMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { localStorage.setItem('ai_currency', currency); }, [currency]);

  useEffect(() => {
    try { localStorage.setItem('lyra_seen_reminders', JSON.stringify(seenReminderKeys.slice(0, 500))); } catch {}
  }, [seenReminderKeys]);

  const refreshReminders = async (loadingState = false) => {
    try {
      if (loadingState) setRemindersLoading(true);
      const capUsd = Number(localStorage.getItem('ai_cap') || 50);
      const seen = seenReminderKeys.join(',');
      const data: any = await remindersAPI.list({ sinceMinutes: 180, capUsd, seen });
      if (data?.success) setReminders(Array.isArray(data.reminders) ? data.reminders : []);
    } catch (_) {
      // ignore failed reminder fetches to avoid noisy top bar UX
    } finally {
      if (loadingState) setRemindersLoading(false);
    }
  };

  useEffect(() => {
    refreshReminders(true);
    const timer = window.setInterval(() => refreshReminders(false), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleClickOutsideReminders = (event: MouseEvent) => {
      if (showRemindersMenu && remindersMenuRef.current && !remindersMenuRef.current.contains(event.target as Node)) {
        setShowRemindersMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideReminders);
    return () => document.removeEventListener('mousedown', handleClickOutsideReminders);
  }, [showRemindersMenu]);

  const markReminderSeen = (eventKey: string) => {
    if (!eventKey) return;
    setSeenReminderKeys((prev) => (prev.includes(eventKey) ? prev : [...prev, eventKey]));
    setReminders((prev) => prev.filter((r) => r.eventKey !== eventKey));
  };

  const clearAllReminders = () => {
    const keys = reminders.map((r) => r.eventKey).filter(Boolean);
    if (keys.length === 0) return;
    setSeenReminderKeys((prev) => [...prev, ...keys.filter((k) => !prev.includes(k))]);
    setReminders([]);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showProfileMenu && profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  const handleProfileMenuClick = (action: 'profile' | 'subscribed' | 'logout') => {
    setShowProfileMenu(false);
    if (action === 'profile') navigate('/settings/profile');
    else if (action === 'subscribed') setShowUpgradeModal(true);
    else if (action === 'logout') {
      if (onLogout) {
        onLogout();
      } else {
        try { Object.keys(localStorage).filter((k) => k.startsWith('lyra:domainAgentSelection:')).forEach((k) => localStorage.removeItem(k)); } catch { /* storage unavailable */ }
        localStorage.removeItem('authToken');
        clearCostSummary();
        localStorage.removeItem('googleAuthToken');
        window.location.href = '/';
      }
    }
  };

  // Reminders bell + profile avatar — identical markup reused in both the
  // desktop header and the mobile fixed header (only one is ever mounted at
  // a time, so the shared refs/state stay unambiguous).
  const bellAndProfile = (
    <>
      <div style={{ position: 'relative' }} ref={remindersMenuRef}>
        <button
          onClick={() => setShowRemindersMenu((v) => !v)}
          title="Reminders"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer',
            background: showRemindersMenu ? (T2.accentSoft) : 'transparent', color: barDark.ink2,
          }}
        >
          <div style={{ position: 'relative' }}>
            <Bell size={16} />
            {reminders.length > 0 && (
              <span style={{
                position: 'absolute', top: -6, right: -8, minWidth: 14, height: 14, padding: '0 3px',
                borderRadius: 999, background: 'var(--lyra-color-danger)', color: 'var(--app-on-accent)', fontSize: 9, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {reminders.length > 9 ? '9+' : reminders.length}
              </span>
            )}
          </div>
        </button>

        {showRemindersMenu && (
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 8, width: 'min(340px, calc(100vw - 24px))',
            background: barDark.bg, border: `1px solid ${barDark.border}`, borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)', zIndex: 90, overflow: 'hidden',
          }}>
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${barDark.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: barDark.ink }}>Reminders</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {reminders.length > 0 && (
                  <button
                    onClick={clearAllReminders}
                    style={{
                      border: `1px solid ${isDarkMode ? 'var(--app-text-2)' : '#FBD5D5'}`,
                      background: 'var(--lyra-color-danger-bg)',
                      color: 'var(--lyra-color-danger)',
                      borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Clear all
                  </button>
                )}
                <button
                  onClick={() => refreshReminders(true)}
                  style={{ border: `1px solid ${barDark.border}`, background: barDark.bg, color: barDark.ink2, borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                >
                  Refresh
                </button>
              </div>
            </div>
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {remindersLoading ? (
                <div style={{ padding: '16px 14px', fontSize: 12.5, color: barDark.ink3 }}>Loading reminders...</div>
              ) : reminders.length === 0 ? (
                <div style={{ padding: '16px 14px', fontSize: 12.5, color: barDark.ink3 }}>No reminders right now.</div>
              ) : (
                reminders.map((reminder, index) => (
                  <div key={`${reminder.eventKey || 'rem'}-${index}`} style={{ padding: '10px 14px', borderBottom: `1px solid ${barDark.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: barDark.ink }}>{reminder.title || 'Reminder'}</div>
                        <div style={{ fontSize: 11.5, color: barDark.ink3, marginTop: 2 }}>{reminder.message}</div>
                      </div>
                      <button
                        onClick={() => markReminderSeen(reminder.eventKey)}
                        style={{ flexShrink: 0, border: 'none', background: 'none', color: barDark.ink3, fontSize: 10.5, cursor: 'pointer' }}
                      >
                        Dismiss
                      </button>
                    </div>
                    {reminder.link && (
                      <button
                        onClick={() => { navigate(reminder.link); setShowRemindersMenu(false); markReminderSeen(reminder.eventKey); }}
                        style={{ marginTop: 8, border: 'none', borderRadius: 6, padding: '4px 9px', background: T2.accent, color: 'var(--app-on-accent)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                      >
                        Open
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {userProfile && (
        <div style={{ position: 'relative' }} ref={profileMenuRef}>
          <button
            onClick={() => setShowProfileMenu((v) => !v)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: 'none', borderRadius: 8, cursor: 'pointer', padding: 3, lineHeight: 0,
              background: showProfileMenu ? (T2.accentSoft) : 'transparent',
            }}
            title={`${userProfile?.name || 'Profile'} — Profile menu`}
          >
            <div style={{ position: 'relative' }}>
              <Avatar src={userProfile?.profilePicture} name={userProfile?.name || formattedProfile?.initials} size={28} style={{ background: T2.accent, color: 'var(--app-on-accent)' }} />
              <span style={{ position: 'absolute', bottom: -1, right: -1, width: 8, height: 8, borderRadius: '50%', background: 'var(--lyra-color-success)', border: '1.5px solid var(--lyra-color-surface)' }} />
            </div>
          </button>

          {showProfileMenu && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 8, width: 208,
              background: barDark.bg, border: `1px solid ${barDark.border}`, borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)', zIndex: 90, overflow: 'hidden',
            }}>
              <div style={{ padding: '12px 14px', borderBottom: `1px solid ${barDark.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                <Avatar src={userProfile?.profilePicture} name={userProfile?.name || formattedProfile?.initials} size={32} style={{ background: T2.accent, color: 'var(--app-on-accent)', flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: barDark.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userProfile?.name}</div>
                  <div style={{ fontSize: 11, color: barDark.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userProfile?.email}</div>
                </div>
              </div>
              <div style={{ padding: 4 }}>
                <button
                  onClick={() => handleProfileMenuClick('profile')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, border: 'none', background: 'none', padding: '8px 10px', borderRadius: 7, fontSize: 12.5, color: barDark.ink2, cursor: 'pointer', textAlign: 'left' }}
                >
                  <User size={14} /> View Profile
                </button>
                <button
                  onClick={() => handleProfileMenuClick('subscribed')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, border: 'none', background: 'none', padding: '8px 10px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, color: T2.accentText, cursor: 'pointer', textAlign: 'left' }}
                >
                  <Crown size={14} color="var(--lyra-color-warning)" /> Upgrade to Pro
                </button>
                <div style={{ height: 1, background: barDark.border, margin: '4px 0' }} />
                <button
                  onClick={() => handleProfileMenuClick('logout')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, border: 'none', background: 'none', padding: '8px 10px', borderRadius: 7, fontSize: 12.5, color: 'var(--lyra-color-danger)', cursor: 'pointer', textAlign: 'left' }}
                >
                  <LogOut size={14} /> Logout
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div style={{ display: 'flex', height: '100vh', background: T2.page, overflow: 'hidden' }}>
      {/* ── Desktop sidebar ── */}
      {!isMobile && (
        <aside
          style={{
            width: isMenuCollapsed ? 80 : 232,
            flexShrink: 0,
            background: sidebarDark.bg,
            borderRight: `1px solid ${sidebarDark.border}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'width 220ms ease-in-out, background 150ms ease',
          }}
        >
          <div
            style={{
              height: 64, flexShrink: 0, display: 'flex', alignItems: 'center',
              justifyContent: isMenuCollapsed ? 'center' : 'space-between',
              padding: isMenuCollapsed ? '0 8px' : '0 12px',
              borderBottom: `1px solid ${sidebarDark.border}`,
              transition: 'padding 220ms ease-in-out',
            }}
          >
            {isMenuCollapsed ? (
              // Collapsed: one centered button — the logo doubles as the
              // expand control, same pattern as Layout.tsx's sidebar (avoids
              // cramming a logo + separate chevron into the narrow width,
              // which is what made that button clip/flicker before).
              <button
                onClick={toggleMenuCollapsed}
                aria-label="Expand menu"
                title="Expand menu"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, border: 'none', background: 'transparent', borderRadius: 10, cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = sidebarDark.hoverBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <img src="/images/lyra-icon.png" alt="Lyra" style={{ height: 32, width: 'auto' }} />
              </button>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <img src="/images/lyra-icon.png" alt="Lyra" style={{ height: 40, width: 'auto', flexShrink: 0 }} />
                  <span style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.025em', color: sidebarDark.title }}>Lyra</span>
                </div>
                <button
                  onClick={toggleMenuCollapsed}
                  aria-label="Collapse menu"
                  title="Collapse menu"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, flexShrink: 0, border: 'none', background: 'transparent', color: sidebarDark.icon, borderRadius: 8, cursor: 'pointer', transition: 'background 150ms ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = sidebarDark.hoverBg; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <ChevronLeft size={16} />
                </button>
              </>
            )}
          </div>

          <PrimarySidebarNav collapsed={isMenuCollapsed} isDarkMode={isDarkMode} onToggleDarkMode={toggleDarkMode} style={{ flex: 1, minHeight: 0 }} />
        </aside>
      )}

      {/* ── Mobile: fixed top bar + slide-in nav drawer ── */}
      {isMobile && (
        <>
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, height: 56, zIndex: 60,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0 12px', background: T2.surface, borderBottom: `1px solid ${T2.border}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {backTo ? (
                <button
                  onClick={() => navigate(backTo)}
                  title="Back"
                  style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: T2.ink2, borderRadius: 8, cursor: 'pointer', flexShrink: 0 }}
                >
                  <ArrowLeft size={17} />
                </button>
              ) : (
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  aria-label="Open navigation"
                  title="Menu"
                  style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: T2.ink2, borderRadius: 8, cursor: 'pointer', flexShrink: 0 }}
                >
                  <PanelLeft size={18} />
                </button>
              )}
              <span style={{ fontSize: 14, fontWeight: 700, color: T2.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pageTitle || 'Lyra'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              {bellAndProfile}
            </div>
          </div>

          {isMobileMenuOpen && (
            <div
              onClick={() => setIsMobileMenuOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 70 }}
            />
          )}

          <aside
            style={{
              position: 'fixed', top: 0, left: 0, bottom: 0, width: 288, maxWidth: '86vw', zIndex: 80,
              background: sidebarDark.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden',
              transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-105%)',
              transition: 'transform 220ms ease, background 150ms ease',
              boxShadow: '3px 0 24px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ height: 56, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px', borderBottom: `1px solid ${sidebarDark.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src="/images/lyra-icon.png" alt="Lyra" style={{ width: 28, height: 28 }} />
                <span style={{ fontSize: 15, fontWeight: 600, color: sidebarDark.title }}>Lyra</span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close navigation"
                style={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: sidebarDark.icon, borderRadius: 8, cursor: 'pointer' }}
              >
                <XIcon size={16} />
              </button>
            </div>
            <PrimarySidebarNav isDarkMode={isDarkMode} onToggleDarkMode={toggleDarkMode} style={{ flex: 1, minHeight: 0 }} />
          </aside>
        </>
      )}

      {/* ── Main column ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {!isMobile && (
          <div
            style={{
              height: 56,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              padding: '0 24px',
              borderBottom: `1px solid ${barDark.border}`,
              background: barDark.bg,
              transition: 'background 150ms ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              {backTo && (
                <button
                  onClick={() => navigate(backTo)}
                  title="Back"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, borderRadius: 8, border: `1px solid ${barDark.border}`,
                    background: barDark.bg, color: barDark.ink2, cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  <ArrowLeft size={14} />
                </button>
              )}
              <div style={{ fontSize: 14, fontWeight: 700, color: barDark.ink }}>{pageTitle || ''}</div>
            </div>

            <div style={{ flex: 1 }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative', flexShrink: 0 }}>
              {/* Search — expands to the left, same menu-item search TopBar.tsx has */}
              <div ref={searchContainerRef} style={{ position: 'relative' }}>
                <div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    border: `1px solid ${'var(--app-border-strong)'}`,
                    background: 'var(--lyra-color-surface)',
                    borderRadius: 8, padding: isSearchExpanded ? '6px 10px' : '0',
                    width: isSearchExpanded ? 260 : 0,
                    overflow: 'hidden',
                    opacity: isSearchExpanded ? 1 : 0,
                    transition: 'width 220ms ease, opacity 180ms ease, padding 220ms ease',
                  }}
                >
                  <Search size={15} color={'var(--lyra-color-text-muted)'} style={{ flexShrink: 0 }} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="What are you looking for..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{
                      flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent',
                      fontSize: 13, color: 'var(--lyra-color-text)',
                    }}
                  />
                  {searchQuery && (
                    <button
                      onClick={handleSearchClear}
                      style={{ display: 'flex', border: 'none', background: 'transparent', cursor: 'pointer', padding: 3, borderRadius: 999, color: 'var(--lyra-color-text-muted)', flexShrink: 0 }}
                    >
                      <XIcon size={13} />
                    </button>
                  )}
                </div>

                {isSearchExpanded && showSearchResults && searchResults.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 300, maxHeight: 360, overflowY: 'auto',
                    background: barDark.surface, border: `1px solid ${barDark.border}`, borderRadius: 10,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.12)', zIndex: 95,
                  }}>
                    {searchResults.map((result, idx) => (
                      <div
                        key={`${result.path}-${idx}`}
                        onClick={() => handleSearchResultClick(result)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer',
                          borderBottom: idx === searchResults.length - 1 ? 'none' : `1px solid ${barDark.border}`,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = barDark.hoverBg; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <ExternalLink size={14} color={barDark.ink3} style={{ flexShrink: 0 }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: barDark.ink }}>{result.label}</div>
                          {result.category && <div style={{ fontSize: 11, color: barDark.ink3, marginTop: 1 }}>{result.category}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {isSearchExpanded && showSearchResults === false && searchQuery.length > 0 && searchResults.length === 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 260, padding: '10px 14px',
                    background: barDark.surface, border: `1px solid ${barDark.border}`, borderRadius: 10,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.12)', zIndex: 95, fontSize: 12, color: barDark.ink3,
                  }}>
                    No menu items found for "{searchQuery}"
                  </div>
                )}
              </div>
              <button
                onClick={handleSearchToggle}
                title="Search"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: isSearchExpanded ? barDark.hoverBg : 'transparent', color: barDark.ink2, flexShrink: 0,
                }}
              >
                <Search size={16} />
              </button>

              <button
                ref={usageBtnRef}
                onClick={() => setShowUsage((v) => { const nv = !v; if (nv) fetchCostSummary(); return nv; })}
                title="Cost & Budget"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  border: `1px solid ${showUsage ? (T2.accent) : (T2.border)}`,
                  background: showUsage ? (T2.accentSoft) : (T2.surface),
                  borderRadius: 999,
                  padding: '5px 12px',
                  cursor: 'pointer',
                }}
              >
                {/* Currency toggle — stops propagation so it doesn't also toggle the popover */}
                <span
                  role="button"
                  onClick={(e) => { e.stopPropagation(); setCurrency((c) => (c === 'USD' ? 'INR' : 'USD')); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 3, padding: '1px 7px',
                    borderRadius: 999, border: `1px solid ${T2.border}`, background: T2.surface,
                    fontSize: 11, fontWeight: 800, cursor: 'pointer',
                  }}
                  title="Toggle USD / INR"
                >
                  <span style={{ opacity: currency === 'USD' ? 1 : 0.35, color: currency === 'USD' ? T2.accentText : undefined }}>$</span>
                  <span style={{ color: T2.ink3, fontSize: 9.5 }}>|</span>
                  <span style={{ opacity: currency === 'INR' ? 1 : 0.35, color: currency === 'INR' ? T2.accentText : undefined }}>₹</span>
                </span>

                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: T2.ink}}>
                    {!costHasData && costLoading ? '…' : (() => {
                      const ce = costData?.summary?.totalEstimatedCostUSD ?? 0;
                      return currency === 'USD' ? `$${Number(ce).toFixed(3)}` : `₹${(Number(ce) * 83.5).toFixed(0)}`;
                    })()}
                  </span>
                  <span style={{ fontSize: 9.5, color: T2.ink3, fontWeight: 600 }}>
                    {currency === 'USD'
                      ? `cap $${localStorage.getItem('ai_cap') ?? '50'}`
                      : `cap ₹${(Number(localStorage.getItem('ai_cap') ?? 50) * 83.5).toFixed(0)}`}
                  </span>
                </span>

                <ChevronDown size={12} color={T2.ink3} style={{ transform: showUsage ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {showUsage && (
                <CostBudgetPopover
                  anchorRef={usageBtnRef}
                  onClose={() => setShowUsage(false)}
                  currency={currency}
                  data={costData}
                  loading={costLoading}
                  onRefresh={fetchCostSummary}
                />
              )}
              {bellAndProfile}
            </div>
          </div>
        )}

        <main style={{ flex: 1, overflowY: 'auto', paddingTop: isMobile ? 56 : 0 }}>{children}</main>
      </div>

      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} isDarkMode={false} />
    </div>
  );
}
